import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from './auth.service';

export type UserRole =
  | 'ESTOKAI_OWNER'
  | 'dono'
  | 'gerente'
  | 'vendedor'
  | 'owner'
  | 'financial_manager'
  | 'sales_user'
  | 'stock_manager'
  | 'partner';

export interface ProfileViewConfig {
  role: UserRole;
  displayName: string;
  description: string;
  availableFeatures: string[];
  restrictedFeatures: string[];
  dashboardConfig: {
    showSalesMetrics: boolean;
    showInventoryMetrics: boolean;
    showFinancialMetrics: boolean;
    showEmployeeMetrics: boolean;
    showCustomerMetrics: boolean;
    showReportsAccess: boolean;
    canManageUsers: boolean;
    canManageIntegrations: boolean;
    canViewAllStores: boolean;
    defaultCharts: string[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class ProfileViewService {
  private currentViewProfile = new BehaviorSubject<UserRole>('ESTOKAI_OWNER');
  private isViewModeActive = new BehaviorSubject<boolean>(false);

  // Configurações de visualização por perfil
  private profileConfigs: Record<UserRole, ProfileViewConfig> = {
    ESTOKAI_OWNER: {
      role: 'ESTOKAI_OWNER',
      displayName: 'Super Admin',
      description: 'Acesso total ao sistema e gerenciamento de todas as lojas',
      availableFeatures: [
        'dashboard',
        'products',
        'categories',
        'brands',
        'sales',
        'suppliers',
        'reports',
        'users',
        'integrations',
        'campaigns',
        'analytics',
        'billing',
      ],
      restrictedFeatures: [],
      dashboardConfig: {
        showSalesMetrics: true,
        showInventoryMetrics: true,
        showFinancialMetrics: true,
        showEmployeeMetrics: true,
        showCustomerMetrics: true,
        showReportsAccess: true,
        canManageUsers: true,
        canManageIntegrations: true,
        canViewAllStores: true,
        defaultCharts: [
          'sales-overview',
          'inventory-status',
          'financial-summary',
          'store-performance',
        ],
      },
    },
    dono: {
      role: 'dono',
      displayName: 'Dono da Loja',
      description: 'Proprietário da loja com controle total sobre seu negócio',
      availableFeatures: [
        'dashboard',
        'products',
        'categories',
        'brands',
        'sales',
        'suppliers',
        'reports',
        'users',
        'integrations',
        'campaigns',
      ],
      restrictedFeatures: ['billing', 'analytics'],
      dashboardConfig: {
        showSalesMetrics: true,
        showInventoryMetrics: true,
        showFinancialMetrics: true,
        showEmployeeMetrics: true,
        showCustomerMetrics: true,
        showReportsAccess: true,
        canManageUsers: true,
        canManageIntegrations: true,
        canViewAllStores: true, // DONO pode selecionar múltiplas lojas
        defaultCharts: [
          'sales-trend',
          'top-products',
          'profit-margin',
          'store-comparison',
        ],
      },
    },
    gerente: {
      role: 'gerente',
      displayName: 'Gerente',
      description: 'Gerenciamento operacional da loja',
      availableFeatures: [
        'dashboard',
        'products',
        'categories',
        'brands',
        'sales',
        'suppliers',
        'reports',
        'campaigns',
      ],
      restrictedFeatures: ['users', 'integrations', 'billing', 'analytics'],
      dashboardConfig: {
        showSalesMetrics: true,
        showInventoryMetrics: true,
        showFinancialMetrics: false, // Gerente não vê métricas financeiras completas
        showEmployeeMetrics: true,
        showCustomerMetrics: true,
        showReportsAccess: true,
        canManageUsers: false,
        canManageIntegrations: false,
        canViewAllStores: false, // Gerente vê apenas sua loja
        defaultCharts: [
          'daily-sales',
          'inventory-alerts',
          'employee-performance',
          'customer-satisfaction',
        ],
      },
    },
    vendedor: {
      role: 'vendedor',
      displayName: 'Vendedor',
      description: 'Foco em vendas e atendimento ao cliente',
      availableFeatures: ['dashboard', 'sales', 'products'],
      restrictedFeatures: [
        'categories',
        'brands',
        'suppliers',
        'reports',
        'users',
        'integrations',
        'campaigns',
        'billing',
        'analytics',
      ],
      dashboardConfig: {
        showSalesMetrics: true,
        showInventoryMetrics: false, // Vendedor vê apenas produtos disponíveis
        showFinancialMetrics: false,
        showEmployeeMetrics: false,
        showCustomerMetrics: true,
        showReportsAccess: false,
        canManageUsers: false,
        canManageIntegrations: false,
        canViewAllStores: false,
        defaultCharts: [
          'my-sales',
          'sales-targets',
          'customer-interactions',
          'product-recommendations',
        ],
      },
    },
    // New role configurations
    owner: {
      role: 'owner',
      displayName: 'Proprietário',
      description: 'Proprietário da loja com acesso administrativo',
      availableFeatures: [
        'dashboard',
        'products',
        'categories',
        'brands',
        'sales',
        'suppliers',
        'reports',
        'users',
        'integrations',
        'campaigns',
        'financial',
      ],
      restrictedFeatures: [],
      dashboardConfig: {
        showSalesMetrics: true,
        showInventoryMetrics: true,
        showFinancialMetrics: true,
        showEmployeeMetrics: true,
        showCustomerMetrics: true,
        showReportsAccess: true,
        canManageUsers: true,
        canManageIntegrations: true,
        canViewAllStores: true,
        defaultCharts: [
          'sales-trend',
          'financial-overview',
          'inventory-status',
          'profit-analysis',
        ],
      },
    },
    financial_manager: {
      role: 'financial_manager',
      displayName: 'Gerente Financeiro',
      description: 'Foco em gestão financeira e relatórios',
      availableFeatures: [
        'dashboard',
        'reports',
        'financial',
        'suppliers',
        'products',
      ],
      restrictedFeatures: ['users', 'integrations'],
      dashboardConfig: {
        showSalesMetrics: true,
        showInventoryMetrics: false,
        showFinancialMetrics: true,
        showEmployeeMetrics: false,
        showCustomerMetrics: false,
        showReportsAccess: true,
        canManageUsers: false,
        canManageIntegrations: false,
        canViewAllStores: false,
        defaultCharts: [
          'financial-overview',
          'cash-flow',
          'accounts-payable',
          'profit-margin',
        ],
      },
    },
    sales_user: {
      role: 'sales_user',
      displayName: 'Usuário de Vendas',
      description: 'Foco em vendas e atendimento ao cliente',
      availableFeatures: ['dashboard', 'sales', 'products'],
      restrictedFeatures: [
        'categories',
        'brands',
        'suppliers',
        'reports',
        'users',
        'integrations',
        'campaigns',
        'financial',
      ],
      dashboardConfig: {
        showSalesMetrics: true,
        showInventoryMetrics: false,
        showFinancialMetrics: false,
        showEmployeeMetrics: false,
        showCustomerMetrics: true,
        showReportsAccess: false,
        canManageUsers: false,
        canManageIntegrations: false,
        canViewAllStores: false,
        defaultCharts: ['my-sales', 'sales-targets', 'customer-interactions'],
      },
    },
    stock_manager: {
      role: 'stock_manager',
      displayName: 'Gerente de Estoque',
      description: 'Foco em gestão de inventário e produtos',
      availableFeatures: [
        'dashboard',
        'products',
        'categories',
        'brands',
        'suppliers',
        'reports',
      ],
      restrictedFeatures: ['users', 'integrations', 'financial'],
      dashboardConfig: {
        showSalesMetrics: false,
        showInventoryMetrics: true,
        showFinancialMetrics: false,
        showEmployeeMetrics: false,
        showCustomerMetrics: false,
        showReportsAccess: true,
        canManageUsers: false,
        canManageIntegrations: false,
        canViewAllStores: false,
        defaultCharts: [
          'inventory-status',
          'stock-alerts',
          'supplier-performance',
        ],
      },
    },
    partner: {
      role: 'partner',
      displayName: 'Sócio',
      description: 'Sócio com acesso a relatórios e métricas',
      availableFeatures: ['dashboard', 'reports', 'financial'],
      restrictedFeatures: [
        'users',
        'integrations',
        'products',
        'categories',
        'brands',
      ],
      dashboardConfig: {
        showSalesMetrics: true,
        showInventoryMetrics: false,
        showFinancialMetrics: true,
        showEmployeeMetrics: false,
        showCustomerMetrics: false,
        showReportsAccess: true,
        canManageUsers: false,
        canManageIntegrations: false,
        canViewAllStores: false,
        defaultCharts: ['sales-trend', 'financial-overview', 'profit-analysis'],
      },
    },
  };

  constructor(private authService: AuthService) {
    // Registrar instância globalmente para acesso pelo AuthService
    (globalThis as any).__profileViewServiceInstance = this;

    // Aguarda a inicialização do AuthService antes de definir perfil padrão
    setTimeout(() => {
      this.initializeDefaultView();
    }, 100);
  }

  private async initializeDefaultView(): Promise<void> {
    try {
      console.log('🔄 Initializing ProfileViewService...');

      // Obter roles reais primeiro
      const userRoles = await this.authService.getUserRoles();
      console.log('🔑 User roles for profile initialization:', userRoles);

      const isEstokaiOwner = userRoles.includes('ESTOKAI_OWNER');

      if (isEstokaiOwner) {
        console.log(
          '✅ ESTOKAI_OWNER detected - setting profile to ESTOKAI_OWNER'
        );
        this.currentViewProfile.next('ESTOKAI_OWNER');
        this.isViewModeActive.next(false); // ESTOKAI_OWNER inicia sem modo visualização
      } else {
        console.log('👤 Regular user detected - using real profile');
        // Para usuários não ESTOKAI_OWNER, usar seu perfil real
        const userRoles = await this.authService.getUserRoles();
        if (userRoles.includes('owner') || userRoles.includes('dono')) {
          this.currentViewProfile.next('owner');
        } else if (
          userRoles.includes('financial_manager') ||
          userRoles.includes('gerente')
        ) {
          this.currentViewProfile.next('financial_manager');
        } else if (
          userRoles.includes('sales_user') ||
          userRoles.includes('vendedor')
        ) {
          this.currentViewProfile.next('sales_user');
        } else if (userRoles.includes('stock_manager')) {
          this.currentViewProfile.next('stock_manager');
        } else if (
          userRoles.includes('partner') ||
          userRoles.includes('socio')
        ) {
          this.currentViewProfile.next('partner');
        }
      }
    } catch (error) {
      console.error('Error initializing profile view:', error);
    }
  }

  // Observables para componentes reagirem
  getCurrentViewProfile(): Observable<UserRole> {
    return this.currentViewProfile.asObservable();
  }

  getIsViewModeActive(): Observable<boolean> {
    return this.isViewModeActive.asObservable();
  }

  // Método para ESTOKAI_OWNER trocar visualização
  async switchProfileView(targetRole: UserRole): Promise<boolean> {
    const isEstokaiOwner = await this.authService.isEstokaiOwner();

    if (!isEstokaiOwner) {
      console.warn('Only ESTOKAI_OWNER can switch profile views');
      return false;
    }

    console.log(
      `Switching profile view from ${this.currentViewProfile.value} to ${targetRole}`
    );

    this.currentViewProfile.next(targetRole);
    this.isViewModeActive.next(targetRole !== 'ESTOKAI_OWNER');

    console.log(
      `Profile view switched to: ${targetRole}, View mode active: ${this.isViewModeActive.value}`
    );

    // Forçar atualização da aplicação (recarregar rota atual)
    this.notifyApplicationUpdate();

    return true;
  }

  // Notificar a aplicação que houve mudança de perfil
  private notifyApplicationUpdate(): void {
    // Emit event para componentes reagirem
    window.dispatchEvent(
      new CustomEvent('profileViewChanged', {
        detail: {
          currentRole: this.currentViewProfile.value,
          isViewModeActive: this.isViewModeActive.value,
        },
      })
    );
  }

  // Resetar para visualização original (ESTOKAI_OWNER)
  async resetToOriginalView(): Promise<void> {
    const isEstokaiOwner = await this.authService.isEstokaiOwner();

    if (isEstokaiOwner) {
      console.log('Resetting to original ESTOKAI_OWNER view');
      this.currentViewProfile.next('ESTOKAI_OWNER');
      this.isViewModeActive.next(false);

      // Notificar a aplicação
      this.notifyApplicationUpdate();
    }
  }

  // Verificar se usuário pode acessar uma funcionalidade na visualização atual
  async canAccessFeature(feature: string): Promise<boolean> {
    const currentRole = this.currentViewProfile.value;
    const config = this.profileConfigs[currentRole];

    // ESTOKAI_OWNER sempre pode acessar tudo, exceto quando em modo visualização
    const isEstokaiOwner = await this.authService.isEstokaiOwner();
    if (isEstokaiOwner && currentRole === 'ESTOKAI_OWNER') {
      return true;
    }

    // Verificar se a funcionalidade está disponível para o perfil atual
    return (
      config.availableFeatures.includes(feature) &&
      !config.restrictedFeatures.includes(feature)
    );
  }

  // Obter configuração do perfil atual
  getCurrentProfileConfig(): ProfileViewConfig {
    const currentRole = this.currentViewProfile.value;
    return this.profileConfigs[currentRole];
  }

  // Obter todos os perfis disponíveis para troca (apenas para ESTOKAI_OWNER)
  async getAvailableProfiles(): Promise<ProfileViewConfig[]> {
    console.log('🔍 Getting available profiles...');

    const userRoles = await this.authService.getUserRoles();
    const isEstokaiOwner = userRoles.includes('ESTOKAI_OWNER');

    console.log('🔑 User roles:', userRoles);
    console.log('👑 Is ESTOKAI_OWNER:', isEstokaiOwner);

    if (!isEstokaiOwner) {
      console.log('❌ Not ESTOKAI_OWNER - no profile switching available');
      return [];
    }

    const profiles = Object.values(this.profileConfigs);
    console.log(
      '✅ Available profiles for ESTOKAI_OWNER:',
      profiles.map((p) => p.displayName)
    );

    return profiles;
  }

  // Verificar se pode trocar perfis
  async canSwitchProfiles(): Promise<boolean> {
    return await this.authService.isEstokaiOwner();
  }

  // Método estático para AuthService acessar role ativa sem dependência circular
  static getActiveRoleForAuth(): UserRole | null {
    // Acessar a instância global se disponível
    const instance = (globalThis as any).__profileViewServiceInstance;
    if (instance) {
      try {
        return instance.currentViewProfile.value;
      } catch (error) {
        console.warn('Error accessing ProfileViewService instance:', error);
      }
    }
    return null;
  }

  static isViewModeActiveForAuth(): boolean {
    const instance = (globalThis as any).__profileViewServiceInstance;
    if (instance) {
      try {
        return instance.isViewModeActive.value;
      } catch (error) {
        console.warn('Error accessing ProfileViewService instance:', error);
      }
    }
    return false;
  }

  // Método para forçar atualização da aplicação
  forceApplicationUpdate(): void {
    console.log('Forcing application update after profile change');

    // Dispatch custom event
    this.notifyApplicationUpdate();

    // Forçar detecção de mudanças nos observables
    const currentRole = this.currentViewProfile.value;
    const isViewActive = this.isViewModeActive.value;

    // Re-emit os valores atuais para forçar update dos subscribers
    setTimeout(() => {
      this.currentViewProfile.next(currentRole);
      this.isViewModeActive.next(isViewActive);
    }, 10);
  }
}
